import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LinkOther } from '@sorare/core/src/atoms/navigation/Box';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { So5RankingRewards } from 'components/ranking/So5RankingRewards';
import {
  ContenderLeftFooterState,
  type ContenderLeftFooterStateReturnType,
} from 'lib/contender';

import type { LineupFooterStatus_lineupInterface } from './__generated__/index.graphql';

const messages = defineMessages({
  scoreToTarget: {
    id: 'Lineup.LiveLineupFooter.scoreToTarget2',
    defaultMessage: '<b>{score}</b> to target',
  },
  scoreToClosestTarget: {
    id: 'Lineup.LiveLineupFooter.RightLiveLineupFooter.scoreToTarget2',
    defaultMessage: '<b>{score}</b> to target',
  },
  scoreToClosestTargetNumber: {
    id: 'Lineup.LiveLineupFooter.RightLiveLineupFooter.scoreToTargetNumber',
    defaultMessage: '<b>{score}</b> to target {number}',
  },
  scoreToClosestReward: {
    id: 'Lineup.LiveLineupFooter.scoreToReward2',
    defaultMessage: '<b>{score}</b> to reward',
  },
});

const StyledBold = styled.b`
  color: var(--c-white);
`;

export const LineupFooterStatus = ({
  leftState,
  so5Lineup,
}: {
  leftState: ContenderLeftFooterStateReturnType;
  so5Lineup?: LineupFooterStatus_lineupInterface;
}) => {
  if (!leftState) {
    return null;
  }
  const { state } = leftState;
  if (
    isType(so5Lineup, 'So5Lineup') &&
    state === ContenderLeftFooterState.REWARDS
  ) {
    return (
      <LinkOther as="div">
        <Horizontal gap={0.5}>
          <So5RankingRewards so5Lineup={so5Lineup} />
        </Horizontal>
      </LinkOther>
    );
  }
  switch (state) {
    case ContenderLeftFooterState.CANCELLED:
      return <Ellipsis>{so5Lineup?.cancelledReason}</Ellipsis>;
    case ContenderLeftFooterState.NOT_STARTED:
      return (
        <FormattedMessage
          id="Lineup.LiveLineupFooter.timeLeftToNextMatch"
          defaultMessage="<b>{timeLeft}</b> to next match"
          values={{
            b: chunks => {
              return <StyledBold>{chunks}</StyledBold>;
            },
            timeLeft: <TimeLeft time={leftState.date} />,
          }}
        />
      );
    case ContenderLeftFooterState.POINTS_TO_CLOSEST_REWARD:
      return (
        <FormattedMessage
          {...messages.scoreToClosestReward}
          values={{
            b: chunks => {
              return <StyledBold>{chunks}</StyledBold>;
            },
            score: (
              <FormattedMessage
                {...fantasy.pointsWithoutFormat}
                values={{
                  score: leftState.points,
                }}
              />
            ),
          }}
        />
      );
    case ContenderLeftFooterState.POINTS_TO_SAFETY:
      return (
        <FormattedMessage
          id="Lineup.LiveLineupFooter.scoreToSafety"
          defaultMessage="<b>{score}</b> to safety"
          values={{
            b: chunks => {
              return <StyledBold>{chunks}</StyledBold>;
            },
            score: (
              <FormattedMessage
                {...fantasy.pointsWithoutFormat}
                values={{
                  b: StyledBold,
                  score: leftState.points,
                }}
              />
            ),
          }}
        />
      );
    case ContenderLeftFooterState.POINTS_TO_TARGET:
      return (
        <FormattedMessage
          {...messages.scoreToTarget}
          values={{
            b: chunks => {
              return <StyledBold>{chunks}</StyledBold>;
            },
            score: (
              <FormattedMessage
                {...fantasy.pointsWithoutFormat}
                values={{
                  score: leftState.points,
                }}
              />
            ),
          }}
        />
      );
    case ContenderLeftFooterState.POINTS_TO_THRESHOLD:
      return (
        <FormattedMessage
          {...(leftState.thresholdNumber > 1
            ? messages.scoreToClosestTargetNumber
            : messages.scoreToClosestTarget)}
          values={{
            b: chunks => {
              return <StyledBold>{chunks}</StyledBold>;
            },
            number: leftState.thresholdNumber,
            score: (
              <FormattedMessage
                {...fantasy.pointsWithoutFormat}
                values={{
                  score: leftState.points,
                }}
              />
            ),
          }}
        />
      );
    default:
      return null;
  }
};

LineupFooterStatus.fragments = {
  lineupFooterStatus: gql`
    fragment LineupFooterStatus_lineupInterface on LineupInterface {
      cancelledReason
      ... on So5Lineup {
        id
        ...So5RankingRewards_so5Lineup
      }
    }
    ${So5RankingRewards.fragments.so5Lineup}
  ` as TypedDocumentNode<LineupFooterStatus_lineupInterface>,
};
